// src/pages/ClaimDossier/modals/AgentPayoutInfoModal.tsx
// Bổ sung — hiển thị thông tin trả thưởng của hồ sơ do đại lý tự lập: đại lý nào trả thưởng, hình
// thức trực tiếp hay chéo, và nếu chéo thì vé thuộc lô vé (phiếu giao) nào, phân cho đại lý nào.
// Dữ liệu lấy từ payout-service GET /PrizeClaimDossier/{id}/agent-payout-info.

import React from 'react';
import { Badge, Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { AgentPayoutInfo, CLAIM_DOSSIER_PAYOUT_TYPE_BADGE } from '../types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('claim_dossier');

interface AgentPayoutInfoModalProps {
  isOpen: boolean;
  loading: boolean;
  info: AgentPayoutInfo | null;
  onClose: () => void;
}

// Hiển thị "Tên (Mã)" nếu có tên, ngược lại "ĐL #Id" — fail-soft khi system-service không trả được tên.
const formatAgent = (name?: string | null, code?: string | null, id?: number | null): string => {
  if (name) return code ? `${name} (${code})` : name;
  if (code) return code;
  return id ? st('dl_id_text', { id: id }) : '—';
};

const AgentPayoutInfoModal: React.FC<AgentPayoutInfoModalProps> = ({ isOpen, loading, info, onClose }) => {
  return (
    <Modal isOpen={isOpen} toggle={onClose} centered>
      <ModalHeader className="bg-light p-3" toggle={onClose}>
        {st('thong_tin_tra_thuong_dai_ly_title')}{info ? ` — ${info.dossierCode}` : ''}
      </ModalHeader>
      <ModalBody>
        {loading || !info ? (
          <div className="text-center py-4">
            <span className="spinner-border text-primary" role="status" aria-hidden="true"></span>
            <p className="text-muted mt-3 mb-0">{st('dang_tai_thong_tin_text')}</p>
          </div>
        ) : (
          <table className="table table-borderless table-sm mb-0">
            <tbody>
              <tr>
                <td className="text-muted ps-0" style={{ width: 160 }}>
                  {st('ve_text_2')}
                </td>
                <td className="pe-0">
                  {info.symbol}-{info.ticketNumber}
                </td>
              </tr>
              <tr>
                <td className="text-muted ps-0">{st('dai_ly_tra_thuong_text')}</td>
                <td className="pe-0 fw-medium">
                  {formatAgent(info.payingAgentName, info.payingAgentCode, info.payingAgentId)}
                </td>
              </tr>
              <tr>
                <td className="text-muted ps-0">{st('hinh_thuc_tra_thuong_text')}</td>
                <td className="pe-0">
                  {info.payoutType ? (
                    <Badge color={CLAIM_DOSSIER_PAYOUT_TYPE_BADGE[info.payoutType]}>
                      {info.payoutTypeName || (info.isCrossPayout ? st('tra_thuong_cheo_text') : st('tra_thuong_truc_tiep_text'))}
                    </Badge>
                  ) : (
                    <span className="text-muted">{st('khong_xac_dinh_text')}</span>
                  )}
                </td>
              </tr>

              {info.isCrossPayout && (
                <>
                  <tr>
                    <td colSpan={2} className="ps-0 pt-3">
                      <h6 className="text-muted fs-12 fw-bold mb-0">{st('ve_tra_thuong_cheo_thuoc_lo_title')}</h6>
                    </td>
                  </tr>
                  <tr>
                    <td className="text-muted ps-0">{st('dai_ly_duoc_phan_ve_text')}</td>
                    <td className="pe-0 fw-medium">
                      {info.ticketOwnerAgentId
                        ? formatAgent(info.ticketOwnerAgentName, info.ticketOwnerAgentCode, info.ticketOwnerAgentId)
                        : st('chua_xac_dinh_ve_con_o_text')}
                    </td>
                  </tr>
                  {info.delivery ? (
                    <>
                      <tr>
                        <td className="text-muted ps-0">{st('lo_ve_phieu_giao_text')}</td>
                        <td className="pe-0 fw-medium">{info.delivery.deliveryCode}</td>
                      </tr>
                      <tr>
                        <td className="text-muted ps-0">{st('ngay_giao_text')}</td>
                        <td className="pe-0">
                          {info.delivery.deliveredAt
                            ? new Date(info.delivery.deliveredAt).toLocaleString('vi-VN')
                            : '—'}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted ps-0">{st('dai_so_da_giao_text')}</td>
                        <td className="pe-0">
                          {info.delivery.symbol} {info.delivery.rangeStart} – {info.delivery.rangeEnd}
                        </td>
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td colSpan={2} className="ps-0 text-muted fs-12">
                        {st('khong_tra_duoc_lo_ve_ve_text')}
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose}>
          {st('dong_button')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AgentPayoutInfoModal;
